import { createAction } from 'redux-act'
import { put, apply, select, call, takeEvery, all } from 'redux-saga/effects'
import { get, omitBy, kebabCase, find } from 'lodash'
import parseLinkHeader from 'parse-link-header'

export const serverErrorAction = createAction('server error', (text) => ({text}));
export const unauthorizedErrorAction = createAction('unauthorized error', (response) => ({response}));

export function listFetchActions(entity) {
  return {
    request: createAction(`fetch ${entity} list`, (params, url=null) => ({params, url})),
    failure: createAction(`${entity} fetch error`, (message, response) => ({message, response})),
    success: createAction(`receive ${entity} list`, (entityList, response) => ({[`${entity}List`]: entityList}), (entityList, response) => {
      let meta = {};

      const linkHeader = get(response, 'headers.link');
      const resultCount = parseInt(get(response, 'headers.x-result-count'), 10);
      if(linkHeader) {
        meta.pagination = parseLinkHeader(linkHeader);
      }

      if(resultCount) {
        meta.resultCount = resultCount;
      }

      return meta;
    })
  }
}

export function entityFetchActions(entity) {
  return {
    request: createAction(`fetch ${entity} entity`, (id, params, url=null) => ({id, params, url})),
    failure: createAction(`${entity} entity fetch error`, (message, response) => ({message, response})),
    success: createAction(`receive ${entity} entity`, (entityItem) => ({[entity]: entityItem})),
  }
}

export function entityOptionsActions(entity) {
  return {
    request: createAction(`fetch ${entity} options`, (id=null, params=null, url=null) => ({id, params, url})),
    failure: createAction(`${entity} options fetch error`, (message, response) => ({message, response})),
    success: createAction(`receive ${entity} options`, (entityItem, id=null) => ({[entity]: entityItem, id})),
  }
}

export function entityUpdateActions(entity) {
  return {
    request: createAction(`update ${entity} entity`, (id, data) => ({id, data})),
    failure: createAction(`${entity} entity update error`, (message, response) => ({message, response})),
    success: createAction(`receive updated ${entity} entity`, (entityItem) => ({[entity]: entityItem})),
  }
}

export function entityCreateActions(entity) {
  return {
    request: createAction(`create ${entity} entity`, (data) => ({data})),
    failure: createAction(`${entity} entity create error`, (message, response) => ({message, response})),
    success: createAction(`receive created ${entity} entity`, (entityItem) => ({[entity]: entityItem})),
  }
}

export function entityDeleteActions(entity) {
  return {
    request: createAction(`delete ${entity} entity`, (id, entityItem, url=null) => ({id, entityItem, url})),
    failure: createAction(`${entity} entity delete error`, (message, response) => ({message, response})),
    success: createAction(`receive deleted ${entity} entity`, (id, entityItem, url=null) => ({id, [entity]: entityItem, url})),
  }
}

export function * handleError(e, actions) {
  if (e.response) {
    const statusCode = get(e.response, 'status', null);

    if(statusCode === 401) {
      yield put(unauthorizedErrorAction(e.response))
    }

    if(statusCode >= 500) {
      yield put(serverErrorAction(e.response.statusText))
    }

    yield put(actions.failure(e.response.data, e.response))
  }else {
    console.error('error ', e);
  }
}

export function fetchListWorker(route, actions, API, configSelector) {
  return function* fetchEntityList(action, throwError = false, returnResponse = false) {
    try {
      const {url, params} = get(action, 'payload', {});

      const config = yield select(configSelector);
      const api = new API(config);
      let response;

      if(url) {
        response = yield apply(api, api.call, [url, 'GET', null, params]);
      } else {
        response = yield apply(api, api['fetchList'], [route, params]);
      }

      yield put(actions.success(response.data, response));

      return returnResponse ? response : response.data;
    } catch (e) {
      yield call(handleError, e, actions);
      if(throwError) {
        throw e;
      }else {
        return e;
      }
    }
  }
}


export function fetchEntityWorker(route, actions, API, configSelector) {
  return function* fetchEntity(action, throwError = false) {
    try {
      const {id, url, params} = get(action, 'payload', {});

      const config = yield select(configSelector);
      const api = new API(config);
      let response;
      if(url) {
        response = yield apply(api, api.call, [url, 'GET', null, params]);
      } else {
        response = yield apply(api, api[`fetchEntity`], [route, id, params]);
      }

      yield put(actions.success(response.data));

      return response.data;
    } catch (e) {
      yield call(handleError, e, actions);
      if(throwError) {
        throw e;
      }else {
        return e;
      }
    }
  }
}

export function fetchOptionsWorker(route, actions, API, configSelector) {
  return function* fetchEntity(action) {
    try {
      const {id, url, params} = get(action, 'payload', {});

      const config = yield select(configSelector);
      const api = new API(config);
      let response;
      if(url) {
        response = yield apply(api, api.call, [url, 'OPTIONS', null, params]);
      } else if(id) {
        response = yield apply(api, api[`fetchOptions`], [route, id, params]);
      } else {
        response = yield apply(api, api[`fetchRouteOptions`], [route, params]);
      }

      yield put(actions.success(response.data, id));

      return response.data;
    } catch (e) {
      yield call(handleError, e, actions);
      return e;
    }
  }
}

export function updateEntityWorker(route, actions, API, configSelector) {
  return function* updateEntity(action, throwError = false) {
    try {
      const {id, data} = get(action, 'payload', {});

      const config = yield select(configSelector);
      const api = new API(config);
      const response = yield apply(api, api[`updateEntity`], [route, id, data]);

      yield put(actions.success(response.data));

      return response.data;
    } catch (e) {
      yield call(handleError, e, actions);
      if(throwError) {
        throw e;
      }else {
        return e;
      }
    }
  }
}

export function createEntityWorker(route, actions, API, configSelector) {
  return function* createEntity(action, throwError = false) {
    try {
      const {data} = get(action, 'payload', {});

      const config = yield select(configSelector);
      const api = new API(config);
      const response = yield apply(api, api[`createEntity`], [route, data]);

      yield put(actions.success(response.data));

      return response.data;
    } catch (e) {
      yield call(handleError, e, actions);
      if(throwError) {
        throw e;
      }else {
        return e;
      }
    }
  }
}

export function deleteEntityWorker(route, actions, API, configSelector) {
  return function* deleteEntity(action, throwError = false) {
    try {
      const {id, data, url} = get(action, 'payload', {});

      const config = yield select(configSelector);
      const api = new API(config);
      let response;
      if(url) {
        response = yield apply(api, api.call, [url, 'DELETE']);
      }else {
        response = yield apply(api, api[`deleteEntity`], [route, id, data]);
      }

      yield put(actions.success(id, response.data, url))
    } catch (e) {
      yield call(handleError, e, actions);
      if(throwError) {
        throw e;
      }else {
        return e;
      }
    }
  }
}

export class Entity {
  constructor(entityName, ApiClass, configSelector, {
    route,
    key='url'
  }={}) {
    this.entity = entityName;

    let routeName = route ? route : `${kebabCase(entityName)}s`;

    const { entity } = this;
    this.actions = {
      fetchList: listFetchActions(entity),
      fetch: entityFetchActions(entity),
      update: entityUpdateActions(entity),
      create: entityCreateActions(entity),
      del: entityDeleteActions(entity),
      options: entityOptionsActions(entity),
    };

    const { fetchList, fetch, update, create, del, options } = this.actions;
    this.workers = {
      fetchList: fetchListWorker(routeName, fetchList, ApiClass, configSelector),
      fetch: fetchEntityWorker(routeName, fetch, ApiClass, configSelector),
      update: updateEntityWorker(routeName, update, ApiClass, configSelector),
      create: createEntityWorker(routeName, create, ApiClass, configSelector),
      del: deleteEntityWorker(routeName, del, ApiClass, configSelector),
      options: fetchOptionsWorker(routeName, options, ApiClass, configSelector),
    };

    this.saga = [
      takeEvery(fetchList.request().type, this.workers.fetchList),
      takeEvery(fetch.request().type, this.workers.fetch),
      takeEvery(update.request().type, this.workers.update),
      takeEvery(create.request().type, this.workers.create),
      takeEvery(del.request().type, this.workers.del),
      takeEvery(options.request().type, this.workers.options),
    ];

    this.reducer = {
      [fetchList.success]: (state, payload) => {
        if(payload[`${entity}List`]) {
          let newState = Object.assign({}, state);
          payload[`${entity}List`].forEach(object => newState[object[key]] = object);
          return newState
        }
        return state;
      },
      [fetch.success]: (state, payload) =>  Object.assign({}, state, {[payload[entity][key]]: payload[entity]}),
      [update.success]: (state, payload) =>  Object.assign({}, state, {[payload[entity][key]]: payload[entity]}),
      [create.success]: (state, payload) =>  Object.assign({}, state, {[payload[entity][key]]: payload[entity]}),
      [del.success]: (state, payload) =>  {
        const { id, url } = payload || {};
        if(id) {
          const deleted = find(state, {id: payload.id});

          if(deleted) {
            return omitBy(state, (item) => {
              //console.log(item.id, payload.id);
              return item[key] === deleted[key];
            });
          }
        }else if(url) {
          let newState = Object.assign({}, state);
          delete newState[url];

          return newState;
        }

        return state;
      },
    };
  }
}
